import { UserMinus, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Avatar from '@components/ui/Avatar';
import { usePublicConfig } from '@context/PublicConfigContext';
import { resolveProfileDisplaySrc } from '@utils/profileImageSrc';
import { useNotifications } from '@context/NotificationContext';
import api from '@utils/api';
import { formatInboxTime } from '@utils/helpers';
import { INBOX_READ_ROW, INBOX_ROW_FRAME, INBOX_UNREAD_ROW } from '@components/messages/inboxRowStyles';

const JoinInboxCard = ({ n, onJoinRead, isPulsing = false }) => {
  const { siteOrigin } = usePublicConfig();
  const { refreshUnreadCount } = useNotifications();
  const navigate = useNavigate();
  const isLeave = n.type === 'leave';
  const sender = n.senderId;
  const msg = (n.message || '').trim();
  const personName = (() => {
    const fromSender = (sender?.name || '').trim();
    if (fromSender) return fromSender;
    if (isLeave && msg.includes(' left "')) {
      return msg.split(' left "')[0].trim() || 'Someone';
    }
    if (!isLeave && msg.includes(' joined "')) {
      return msg.split(' joined "')[0].trim() || 'Someone';
    }
    return 'Someone';
  })();
  const activityTitle = n.activityId?.title || 'Vibe';
  const activityNavId = n.activityId?._id ?? n.activityId;
  const notifId = n._id ?? n.id;

  const handleClick = async () => {
    try {
      if (!n.isRead && notifId) {
        await api.patch(`/notifications/${notifId}/read`);
        await onJoinRead?.(notifId);
      }
    } catch {
      /* still refresh + navigate */
    }
    await refreshUnreadCount();
    if (activityNavId) {
      navigate(`/activity/${activityNavId}`);
    }
  };

  const unread = !n.isRead;

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${INBOX_ROW_FRAME} ${unread ? INBOX_UNREAD_ROW : INBOX_READ_ROW} ${
        isPulsing ? 'animate-pulse-glow' : ''
      }`}
    >
      <div className="relative shrink-0">
        <Avatar name={sender?.name} src={resolveProfileDisplaySrc(sender, siteOrigin)} size="md" />
        <span
          className={`absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full text-white shadow ring-2 ring-white dark:ring-surface-900 ${
            isLeave ? 'bg-amber-500' : 'bg-emerald-500'
          }`}
        >
          {isLeave ? (
            <UserMinus size={11} strokeWidth={2.5} />
          ) : (
            <UserPlus size={11} strokeWidth={2.5} />
          )}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-surface-900 dark:text-surface-50">
            <span
              className={
                isLeave
                  ? 'text-amber-800 dark:text-amber-300'
                  : 'text-emerald-700 dark:text-emerald-400'
              }
            >
              {isLeave ? `${personName} left` : 'New join'}
            </span>
            <span className="mx-1.5 font-normal text-surface-400 dark:text-surface-500">·</span>
            <span className="font-medium text-surface-800 dark:text-surface-200">{activityTitle}</span>
          </p>
          <span className="shrink-0 text-xs text-surface-400">{formatInboxTime(n.createdAt)}</span>
        </div>
        <p className="mt-1 truncate text-sm text-surface-500 dark:text-surface-400">
          {msg ||
            (isLeave
              ? `${personName} left your vibe`
              : `${personName} joined your vibe`)}
        </p>
      </div>
    </button>
  );
};

export default JoinInboxCard;
